import {zodResolver} from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import {useEffect, useState} from 'react';
import {useForm, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {Form} from '@/components/ui/form';
import {useToast} from '@/components/ui/use-toast';
import {useAuthorize} from '@/hooks/useAuth';
import {graphqlClient} from '@/services/graphqlClient';
import {
  type DosageDetails,
  type MedicationForm,
  useCreateResidentMedicationMutation,
  useGetResidentAllMedicationsQuery,
  useGetResidentCurrentMedicationsQuery,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';
import {TIME_FORMATS} from '@/utils/timeFormats';

import {
  addDosage,
  findDosage,
  removeDosage,
  updateDosage,
} from '../utils/dosageCrud';
import type {ResidentMedicationBasicFormValues} from '../utils/medicationFormInputs';
import {
  mapResidentMedicationBasicFormValuesToCreateResidentMedicationInput,
  residentMedicationBasicFormDefaultValues,
  residentMedicationBasicFormSchema,
} from '../utils/medicationFormInputs';
import type {DosageDetailsWithId} from '../utils/medicationTypes';
import DosageDialog from './DosageDialog';
import ResidentMedicationBaseFormFields from './ResidentMedicationBaseFormFields';

const CREATE = 'create';

export type AddResidentMedicationDialogProps = DialogProps & {
  showResident?: boolean;
};

const AddResidentMedicationDialog = ({
  onOpenChange,
  showResident,
  ...props
}: AddResidentMedicationDialogProps) => {
  const {t} = useTranslation();
  const {toast} = useToast();
  const [searchParams] = useSearchParams();
  const [dosageId, setDosageId] = useState<string>();

  const canAddMedication = useAuthorize('CREATE_MEDICATION');
  const residentId = searchParams.get('residentId') || '';

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: useGetResidentCurrentMedicationsQuery.getKey({
        input: {
          residentId,
          date: dayjs().format(TIME_FORMATS.awsDate),
        },
      }),
    });

    queryClient.invalidateQueries({
      queryKey: useGetResidentAllMedicationsQuery.getKey({
        input: {
          residentId,
        },
      }),
    });
  };

  const createMedication = useCreateResidentMedicationMutation(graphqlClient, {
    onSuccess,
  });

  const form = useForm<ResidentMedicationBasicFormValues>({
    resolver: zodResolver(residentMedicationBasicFormSchema),
    mode: 'onBlur',
    defaultValues: residentMedicationBasicFormDefaultValues({
      residentId,
    }),
  });

  const handleSubmit = async (values: ResidentMedicationBasicFormValues) => {
    try {
      await createMedication.mutateAsync({
        input:
          mapResidentMedicationBasicFormValuesToCreateResidentMedicationInput(
            values
          ),
      });
      toast({
        title: t('Medication added'),
        variant: 'success',
      });
      onOpenChange?.(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!residentId || form.getValues('residentId') === residentId) return;

    form.setValue('residentId', residentId, {
      shouldValidate: true,
    });
  }, [form, residentId]);

  const handleAddDosage = (dosage: DosageDetails | DosageDetailsWithId) => {
    const dosages = (form.getValues().dosageDetails ||
      []) as DosageDetailsWithId[];

    const newDosages =
      dosageId === CREATE
        ? addDosage(dosage, dosages)
        : updateDosage(
            {...dosage, id: dosageId} as DosageDetailsWithId,
            dosages
          );

    form.setValue('dosageDetails', newDosages, {shouldValidate: true});
  };

  const handleDeleteDosage = (id: string) => {
    const dosages = (form.getValues().dosageDetails ||
      []) as DosageDetailsWithId[];
    form.setValue('dosageDetails', removeDosage(id, dosages), {
      shouldValidate: true,
    });
  };

  const dosageToEdit = findDosage(
    dosageId || '',
    (form.getValues().dosageDetails || []) as DosageDetailsWithId[]
  );

  const selectedMedicationForm = useWatch({
    control: form.control,
    name: 'form',
  }) as MedicationForm | undefined;

  return (
    <>
      <Form {...form}>
        <Dialog {...props} onOpenChange={onOpenChange}>
          <DialogContent asChild>
            <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
              <DialogHeader>
                <DialogTitle>{t('Add medication')}</DialogTitle>
              </DialogHeader>
              <DialogSection>
                <ResidentMedicationBaseFormFields
                  showResident={showResident && !residentId}
                  form={form}
                  onAddDosageClick={() => {
                    setDosageId(CREATE);
                  }}
                  onEditDosageClick={id => {
                    setDosageId(id);
                  }}
                  onDeleteDosageClick={handleDeleteDosage}
                />
              </DialogSection>
              <DialogFooter>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => onOpenChange?.(false)}
                >
                  {t('Cancel')}
                </Button>
                <Button
                  disabled={!canAddMedication || createMedication.isPending}
                  type="submit"
                  variant="primary"
                >
                  {t('Submit')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </Form>
      <DosageDialog
        key={dosageId}
        onSubmit={handleAddDosage}
        open={!!dosageId}
        dosageDetails={dosageToEdit}
        selectedMedicationForm={selectedMedicationForm}
        onOpenChange={() => {
          setDosageId('');
        }}
      />
    </>
  );
};

export default AddResidentMedicationDialog;
